import {
  EPropertyType,
  ESdChangeReason,
  SpecialDIIds,
  type IComponent,
  type IComponentDefinition,
  type IDisplayItem,
  type IDisplayItemComponent,
  type IProject,
  type IScreenDesign,
  type IScreenDesignDictionary
} from "@kuika/kuika-cl-model";
import { isReactNativeGenerationOffered } from "../../components/design-tools/app-settings/react-native-feature-gate";
import type { IScreenDesignContextState } from "../../contexts/screen-design-context/screen-design-context";
import {
  resolveDeclaredChildAnchor,
  type ComponentDefinitionLookup,
  type IDeclaredChildAnchor
} from "./component-children-host";
import { SKIP_ROW_WRAP_SET } from "./component-drop-constants";
import { isDropTargetComponentType, isPlaceableComponent } from "./component-palette";
import type { ScreenDesignInstance } from "./designer-context";
import { resolveEditTarget } from "./detached-screen-session";
import { KMainFunctions } from "./main-functions";
import {
  findWizardOwnedRegion,
  type OwnershipDictionary,
  type WizardOwnedRegion
} from "./managed-task-screen-ownership";
import { ScreenDesignHelper } from "./screen-design-helper";
import {
  insertComponentIntoHost,
  isAutoCreatedColumnSet,
  isTreeOpRefusal,
  removeComponentFromTree,
  type IInsertComponentResult,
  type IRemoveComponentResult,
  type ITreeOpRefusal
} from "./screen-tree-ops";

/**
 * Placing and removing components on a screen — including one the designer does not have OPEN.
 *
 * ## The browser builds; nothing here talks to a server
 *
 * A new component is constructed by the platform's own chain, which is screen-agnostic because it
 * takes the screen and its dictionary as arguments:
 *
 *   `createNewComponentByName` → `createNewDisplayItemByComponent` → `addDefaultComponentStylings`
 *   → `addChildForItemContainer`
 *
 * The last step is why a Table arrives with its header, row and column already inside it: the
 * catalog declares those children and the platform materialises them. The drag-drop engine
 * (`DragDrop.addChildItemByComponentName`) wraps the same chain, but reaches the tree through the
 * PROVIDER, so it can only ever write the open canvas — which is exactly why this module calls the
 * chain directly and hands it the screen it was asked about.
 *
 * ## What it refuses, and why refusing is the feature
 *
 *   - **An anchor inside a platform-owned region.** The generated form and the decision button
 *     group are rebuilt by the backend on every Apply Screens: a component placed inside them
 *     disappears at the next apply, which reads as data loss rather than a refusal. The region is
 *     decided by `findWizardOwnedRegion` — the same function the write guard uses. Stamped chrome
 *     is deliberately allowed: the platform stamps it for itself and leaves its inside open.
 *   - **An anchor that cannot hold children.** Dropping into a label produces a tree the renderer
 *     cannot lay out.
 *   - **A component the catalog does not offer for this project.** Same rule as the palette, so a
 *     hand-built call cannot place what a human could not pick.
 *   - **Removing something the platform owns.** You may delete your own work, never the wizard's.
 */

export interface IPlacementRefusal {
  error: string;
}

export interface IPlacementResult {
  /** The id of the component that was placed — the platform's own component address. */
  kuikaId: number;
  componentName: string;
  /** The anchor it went into. On a wrapped placement this is the `Col` that was built for it. */
  anchorKuikaId: number;
  /**
   * The OUTERMOST node this placement created, and where it went — KOA-18431.
   *
   * Equal to `kuikaId` / `anchorKuikaId` for an ordinary placement. When the component had to be
   * wrapped (see `wrapInRowCol`) it is the `Row`, because the Row is what the screen gained: a
   * ledger that recorded the component instead would ask the server to create it inside a `Col`
   * the server has never seen.
   */
  insertRootKuikaId: number;
  insertRootAnchorKuikaId: number;
}

export interface IRemovalResult {
  kuikaId: number;
  /** Every id the removal took off the screen, the component itself included. */
  removedKuikaIds: number[];
}

export interface IPlaceComponentInput {
  /** The screen to act on. Omitted → the screen the designer has open. */
  screenId?: string | null;
  /** The component that will hold the new one. */
  anchorKuikaId: number;
  /** Catalog name, as the palette offers it. */
  componentName: string;
}

export interface IRemoveComponentInput {
  screenId?: string | null;
  kuikaId: number;
}

const asOwnership = (dictionary: IScreenDesignDictionary | undefined): OwnershipDictionary | undefined =>
  dictionary as unknown as OwnershipDictionary | undefined;

/** Region an anchor may NOT be in: what the backend regenerates, it also overwrites. */
function refuseOwnedAnchor(
  dictionary: IScreenDesignDictionary | undefined,
  anchorKuikaId: number
): IPlacementRefusal | null {
  const region: WizardOwnedRegion | null = findWizardOwnedRegion(asOwnership(dictionary), String(anchorKuikaId));
  if (region === "form") {
    return {
      error:
        "The generated form is rebuilt on every Apply Screens, so a component placed inside it would " +
        "disappear. Add the field to the form's field list instead, or place the component beside the form."
    };
  }
  if (region === "buttons") {
    return {
      error:
        "The decision buttons are rebuilt from the step's interactions on every Apply Screens, so " +
        "anything placed inside them would disappear. Place the component elsewhere on the screen."
    };
  }
  return null;
}

/**
 * The catalog entry for a component NAME, or undefined when this project has none.
 *
 * Exported because a caller may need the definition BEFORE the component exists — a placement that
 * would ship a control nobody can use is refused rather than made and then reported (KOA-18431).
 */
export function findCatalogComponent(project: IProject | undefined, componentName: string): IComponent | undefined {
  const components: IComponent[] | undefined = project?.componentSetObj?.components;
  if (components === undefined) return undefined;
  const wanted: string = componentName.trim().toLowerCase();
  return components.find((candidate: IComponent): boolean => (candidate.name ?? "").trim().toLowerCase() === wanted);
}

/**
 * KOA-18431 — A CONTROL DROPPED ON THE SCREEN BODY NEEDS THE GRID THE PLATFORM PUTS IT IN.
 *
 * The platform's own add path refuses to put most components straight into the body: when the drop
 * target is the root, `component-tools.ts` builds `Row(full width) → Col` first and places into the
 * Col (`shouldAutoWrapInRowCol` → `addWithRowColWrap`). The exceptions are listed once, in
 * `SKIP_ROW_WRAP_SET` — block-level hosts like `Table`, `Panel`, `Form`, `VerticalStack`, which lay
 * themselves out.
 *
 * This surface skipped that step, and the cost is not cosmetic. Live `5609cb0d`: a category filter
 * was placed, its five choices were written, every line landed — and the SelectBox was NOWHERE on
 * the screen, while the Table beside it (in the skip set) rendered normally. A control with no Col
 * has no column width, so the grid gives it none.
 *
 * The platform's wrap cannot be reused as-is: its second step goes through
 * `dragDrop.setColumnsNumber`, which reads the screen the designer has OPEN, so calling it while
 * composing a DETACHED screen would write the user's canvas. What that call does is plain data —
 * bring the Row's `Col` children to the wanted count and write each one's column fraction — so it
 * is done here, over the resolved target, through the same helpers.
 */
interface IRowColWrap {
  rowKuikaId: number;
  colKuikaId: number;
}

function wrapInRowCol(
  sdInstance: ScreenDesignInstance,
  screenId: string | null | undefined,
  anchorKuikaId: number
): IRowColWrap | IPlacementRefusal {
  const placedRow: IInsertComponentResult | ITreeOpRefusal = insertComponentIntoHost(
    resolveEditTarget(sdInstance, screenId),
    sdInstance,
    { hostKuikaId: anchorKuikaId, componentName: "Row" }
  );
  if (isTreeOpRefusal(placedRow)) return { error: placedRow.error };

  // Re-resolved after the insert: the write rebuilt the dictionary, and the Row's own children
  // (the catalog's declared `Col`s) only exist in the rebuilt one.
  const afterRow: IScreenDesignContextState = resolveEditTarget(sdInstance, screenId);
  const row: IDisplayItem | undefined = afterRow.screenDesignDictionary?.[placedRow.kuikaId];
  const declaredCols: IDisplayItem[] = (row?.children ?? []).filter(
    (child: IDisplayItem): boolean => child.item?.name === "Col"
  );

  let colKuikaId: number;
  if (declaredCols.length === 0) {
    const placedCol: IInsertComponentResult | ITreeOpRefusal = insertComponentIntoHost(afterRow, sdInstance, {
      hostKuikaId: placedRow.kuikaId,
      componentName: "Col"
    });
    if (isTreeOpRefusal(placedCol)) return { error: placedCol.error };
    colKuikaId = placedCol.kuikaId;
  } else {
    colKuikaId = declaredCols[0].id;
    // One full-width column, exactly what the platform's own wrap asks for with `[24]`. The extra
    // declared columns are brand new and hold nothing, so removing them takes nothing with them.
    for (const extra of declaredCols.slice(1)) {
      removeComponentFromTree(resolveEditTarget(sdInstance, screenId), extra.id);
    }
  }

  const afterCol: IScreenDesignContextState = resolveEditTarget(sdInstance, screenId);
  setColumnToFullWidth(afterCol, colKuikaId);
  return { rowKuikaId: placedRow.kuikaId, colKuikaId };
}

/**
 * Write the grid's full width into a `Col`'s own column properties.
 *
 * The same property the canvas writes (`EPropertyType.column`) and the same value helper
 * (`getResponsiveColumnStringValue` keeps whatever prefix/suffix the property spells its number
 * with), so a composed Col is indistinguishable from a dragged one. Silent when the catalog gives
 * this Col no column property: there is then nothing to say, and inventing one would be a guess.
 */
function setColumnToFullWidth(target: IScreenDesignContextState, colKuikaId: number): void {
  const col: IDisplayItem | undefined = target.screenDesignDictionary?.[colKuikaId];
  const properties = col?.item?.properties ?? [];
  const gridSystemCount: number = target.project?.componentSetObj?.gridSystemCount ?? 24;
  let wrote: boolean = false;
  for (const property of properties) {
    if (property.selectedType !== EPropertyType.column) continue;
    const current: string | undefined = property.symbol?.fixedValue;
    if (typeof current !== "string" || current.length === 0) continue;
    property.symbol.fixedValue = KMainFunctions.getResponsiveColumnStringValue(current, gridSystemCount);
    wrote = true;
  }
  if (!wrote) return;
  target.setScreenDesign(
    target.getScreenDesign(),
    ESdChangeReason.ChangeResponsiveRowColumnSettings,
    String(colKuikaId)
  );
}

/**
 * Place a catalog component inside `anchorKuikaId` on the given screen.
 *
 * Nothing is persisted: on a detached session the tree lives in memory until Apply Screens sends
 * it, and on the open screen this is an ordinary canvas edit.
 */
export function placeComponent(
  sdInstance: ScreenDesignInstance,
  input: IPlaceComponentInput
): IPlacementResult | IPlacementRefusal {
  const target: IScreenDesignContextState = resolveEditTarget(sdInstance, input.screenId);
  const project: IProject | undefined = target.project;
  if (project?.componentSetObj?.components === undefined) {
    return { error: "The component catalog is not loaded yet." };
  }

  const dictionary: IScreenDesignDictionary | undefined = target.screenDesignDictionary;
  const anchor: IDisplayItem | undefined = dictionary?.[input.anchorKuikaId];
  if (anchor === undefined) {
    return { error: `There is no component ${input.anchorKuikaId} on this screen to place into.` };
  }

  const ownedRefusal: IPlacementRefusal | null = refuseOwnedAnchor(dictionary, input.anchorKuikaId);
  if (ownedRefusal !== null) return ownedRefusal;

  const anchorDefinition: IComponentDefinition | undefined = target.getComponentDefinition?.(anchor.item);
  if (!isDropTargetComponentType(anchorDefinition?.componentType)) {
    return {
      error: `${anchorDefinition?.name ?? "This component"} cannot hold other components. Pick a container.`
    };
  }

  const catalogComponent: IComponent | undefined = findCatalogComponent(project, input.componentName);
  if (catalogComponent === undefined) {
    return { error: `"${input.componentName}" is not in this project's component catalog.` };
  }
  const isWebScreen: boolean = target.getIsWebScreen?.() ?? true;
  if (
    !isPlaceableComponent(catalogComponent, {
      projectType: project.projectType,
      isWebScreen,
      isReportMode: false,
      reactNativeOffered: isReactNativeGenerationOffered()
    })
  ) {
    return { error: `"${input.componentName}" cannot be placed on a screen in this project.` };
  }

  // KOA-18431 — the grid the platform would have put it in. Only at the screen BODY, and only for
  // the components its own add path wraps; everything else goes straight in, as before.
  const goesToScreenBody: boolean = anchor.id === SpecialDIIds.rootDI;
  let host: number = anchor.id;
  let wrapRowKuikaId: number | undefined;
  if (goesToScreenBody && !SKIP_ROW_WRAP_SET.has(input.componentName)) {
    const wrapped: IRowColWrap | IPlacementRefusal = wrapInRowCol(sdInstance, input.screenId, anchor.id);
    if ("error" in wrapped) return wrapped;
    host = wrapped.colKuikaId;
    wrapRowKuikaId = wrapped.rowKuikaId;
  }

  // Report components are never placed from here — this surface builds application screens. The
  // catalog's declared children (a table's header/row/column, a tab's first tab item) are
  // materialised by the shared chain, which is what makes a placed Table a usable table.
  const inserted: IInsertComponentResult | ITreeOpRefusal = insertComponentIntoHost(
    resolveEditTarget(sdInstance, input.screenId),
    sdInstance,
    {
      hostKuikaId: host,
      componentName: input.componentName
    }
  );
  if (isTreeOpRefusal(inserted)) return { error: inserted.error };

  return {
    kuikaId: inserted.kuikaId,
    componentName: input.componentName,
    anchorKuikaId: inserted.hostKuikaId,
    insertRootKuikaId: wrapRowKuikaId ?? inserted.kuikaId,
    insertRootAnchorKuikaId: wrapRowKuikaId === undefined ? inserted.hostKuikaId : anchor.id
  };
}

/**
 * Remove a component the DESIGNER owns from the given screen.
 *
 * Refuses anything the platform generated — including a stamped Header/Footer itself, whose inside
 * is yours but whose existence is not.
 */
export function removeComponent(
  sdInstance: ScreenDesignInstance,
  input: IRemoveComponentInput
): IRemovalResult | IPlacementRefusal {
  const target: IScreenDesignContextState = resolveEditTarget(sdInstance, input.screenId);
  const dictionary: IScreenDesignDictionary | undefined = target.screenDesignDictionary;
  const di: IDisplayItem | undefined = dictionary?.[input.kuikaId];
  if (di === undefined) {
    return { error: `There is no component ${input.kuikaId} on this screen.` };
  }

  const region: WizardOwnedRegion | null = findWizardOwnedRegion(asOwnership(dictionary), String(input.kuikaId));
  if (region !== null) {
    return {
      error:
        "This component was generated by the platform, so it cannot be removed here — the next Apply " +
        "Screens would put it back. Change what generates it instead (the form's fields, or the step's interactions)."
    };
  }
  // The platform's own protection, reused rather than restated: a subtree containing an element
  // whose interaction the platform disabled is not deletable. The region test above already covers
  // the wizard's stamps, but this reaches a stamp nested deeper than the region walk looks.
  const screenForCheck: IScreenDesign = target.getScreenDesign();
  if (ScreenDesignHelper.getAnyChildsInteractionDisabled(input.kuikaId, screenForCheck.displayItems)) {
    return {
      error: "This component contains an element the platform owns, so it cannot be removed here."
    };
  }

  // `DragDropDi` is the reason the platform's own removal path commits with — the change is a tree
  // structure change, and nothing downstream distinguishes add from remove.
  const removed: IRemoveComponentResult | ITreeOpRefusal = removeComponentFromTree(target, input.kuikaId);
  if (isTreeOpRefusal(removed)) return { error: removed.error };
  return { kuikaId: removed.kuikaId, removedKuikaIds: removed.removedKuikaIds };
}

export interface IPlaceChildItemInput {
  /** The screen to act on. Omitted → the screen the designer has open. */
  screenId?: string | null;
  /** The component whose declared children are being edited. */
  hostKuikaId: number;
  /** The child component's catalog name, as the host declares it. */
  componentName: string;
  /** True for a band that belongs at the top (a table header). Default is last. */
  placeFirst?: boolean;
}

/**
 * Add one of a host's DECLARED child items — a table column, a menu item, a tab page.
 *
 * Separate from `placeComponent` because the two answer to different rules. `placeComponent` is the
 * palette: it refuses child items precisely because a child item on a screen is an orphan. This is
 * the child-items editor: the only thing it may add is a child item, and WHICH ones are allowed is
 * not a judgement — the catalog states them in the host's `childrenInfo`, and that statement is the
 * gate. Anything else, including a component that would be perfectly placeable on a screen, is
 * refused here.
 *
 * The host the caller names is not always the host the catalog means — a Table's columns belong to
 * its `TableRow` — so the anchor is resolved through `resolveDeclaredChildAnchor` and the result is
 * reported in `anchorKuikaId`, which is what a staged insert must address.
 */
export function placeChildItem(
  sdInstance: ScreenDesignInstance,
  input: IPlaceChildItemInput
): IPlacementResult | IPlacementRefusal {
  const target: IScreenDesignContextState = resolveEditTarget(sdInstance, input.screenId);
  const project: IProject | undefined = target.project;
  if (project?.componentSetObj?.components === undefined) {
    return { error: "The component catalog is not loaded yet." };
  }

  const dictionary: IScreenDesignDictionary | undefined = target.screenDesignDictionary;
  const host: IDisplayItem | undefined = dictionary?.[input.hostKuikaId];
  if (host === undefined) {
    return { error: `There is no component ${input.hostKuikaId} on this screen to add to.` };
  }

  const ownedRefusal: IPlacementRefusal | null = refuseOwnedAnchor(dictionary, input.hostKuikaId);
  if (ownedRefusal !== null) return ownedRefusal;

  // WHERE the child goes is the catalog's answer, and it is not always the component the caller
  // named: a Table declares `TableHeader`/`TableRow`, and the COLUMNS are the row's. Asked here
  // rather than by each caller, so the DSL and the render path give the same answer — see
  // `resolveDeclaredChildAnchor`.
  const getDefinition: ComponentDefinitionLookup = (item: IDisplayItemComponent | undefined) =>
    target.getComponentDefinition?.(item);
  const anchor: IDeclaredChildAnchor | undefined = resolveDeclaredChildAnchor(host, input.componentName, getDefinition);
  if (anchor === undefined) {
    const hostDefinition: IComponentDefinition | undefined = target.getComponentDefinition?.(host.item);
    return {
      error: `${hostDefinition?.name ?? "This component"} does not have "${input.componentName}" among its items.`
    };
  }
  const anchorDi: IDisplayItem = anchor.host;
  const anchorKuikaId: number = anchorDi.id;

  // KOA-18431 — the table's two STARTING columns come off before the first authored one goes on.
  // The platform gives a fresh TableRow one default and one template column so the table is not an
  // empty box; an author writing its columns out means those three and not five, and the two
  // starters are bound to nothing and headed "Title". The AI's own render path already clears them
  // (`component-tools.ts`); this is the same rule on the screen-agnostic path, recognised by the
  // exact starting shape so a row anyone has worked on is never touched.
  if (isAutoCreatedColumnSet(anchorDi)) {
    anchorDi.children = [];
  }

  const inserted: IInsertComponentResult | ITreeOpRefusal = insertComponentIntoHost(target, sdInstance, {
    hostKuikaId: anchorKuikaId,
    componentName: input.componentName,
    ...(input.placeFirst === true ? { placeFirst: true } : {})
  });
  if (isTreeOpRefusal(inserted)) return { error: inserted.error };

  // A declared child item goes exactly where its host declares it; there is no grid to wrap.
  return {
    kuikaId: inserted.kuikaId,
    componentName: input.componentName,
    anchorKuikaId: inserted.hostKuikaId,
    insertRootKuikaId: inserted.kuikaId,
    insertRootAnchorKuikaId: inserted.hostKuikaId
  };
}

export interface IReorderSiblingInput {
  screenId?: string | null;
  /** The component to move. */
  kuikaId: number;
  /** The sibling it must end up immediately BEFORE. */
  beforeKuikaId: number;
}

export interface IReorderResult {
  kuikaId: number;
  /** The host whose child order changed — what a staged reorder is addressed within. */
  parentKuikaId: number;
}

/**
 * Move a component so it sits immediately before one of its OWN siblings.
 *
 * The child-items editor's up/down buttons are always a sibling swap (it disables drag entirely),
 * so this deliberately refuses anything else rather than reproducing the canvas's general move —
 * that one re-parents, wraps in rows and columns, and reaches the tree through the provider. A
 * cross-parent move asked for here is a caller bug, and answering it by silently doing something
 * else is how a user ends up with a column in another table.
 */
export function reorderSibling(
  sdInstance: ScreenDesignInstance,
  input: IReorderSiblingInput
): IReorderResult | IPlacementRefusal {
  const target: IScreenDesignContextState = resolveEditTarget(sdInstance, input.screenId);
  const dictionary: IScreenDesignDictionary | undefined = target.screenDesignDictionary;
  const moved: IDisplayItem | undefined = dictionary?.[input.kuikaId];
  const before: IDisplayItem | undefined = dictionary?.[input.beforeKuikaId];
  if (moved === undefined || before === undefined) {
    return { error: "One of the components being reordered is no longer on this screen." };
  }
  if (moved.parentId === undefined || moved.parentId === null || moved.parentId !== before.parentId) {
    return { error: "Only components that sit side by side under the same parent can be reordered here." };
  }

  const parent: IDisplayItem | undefined = dictionary?.[moved.parentId];
  if (parent?.children === undefined) {
    return { error: "The parent of the components being reordered could not be found." };
  }
  const ownedRefusal: IPlacementRefusal | null = refuseOwnedAnchor(dictionary, parent.id);
  if (ownedRefusal !== null) return ownedRefusal;

  const without: IDisplayItem[] = parent.children.filter((child: IDisplayItem): boolean => child.id !== moved.id);
  const at: number = without.findIndex((child: IDisplayItem): boolean => child.id === before.id);
  if (at < 0) {
    return { error: "The component to move in front of is no longer among its siblings." };
  }
  without.splice(at, 0, moved);
  parent.children = without;

  const screen: IScreenDesign = target.getScreenDesign();
  target.setScreenDesign(screen, ESdChangeReason.DragDropDi, String(moved.id));
  return { kuikaId: moved.id, parentKuikaId: parent.id };
}

/** Whether a composition call was refused. */
export function isPlacementRefusal(
  result: IPlacementResult | IRemovalResult | IReorderResult | IPlacementRefusal
): result is IPlacementRefusal {
  return (result as IPlacementRefusal).error !== undefined;
}
